import React from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { FitbitReadParticipant } from '../../../services/fitbitCollectionDataService';
import { SYNC_ALERT_DAYS, stoppedCollectionLabel, syncStatus } from './syncStatus';

const who = (participant: FitbitReadParticipant) => `${participant.name} · ${participant.trackingId || participant.participantId}`;

// Freshly checked participants only. A roster cache could be days old and would wrongly flag
// recent syncs. Stopped collections are listed first: no new data arrives until they resume.
export default function FitbitSyncWarning({ participants, now = new Date() }: {
  participants: FitbitReadParticipant[] | null; now?: Date;
}) {
  const checked = (participants || []).filter(participant =>
    participant.enrollmentStatus === 'completed' && participant.availabilityStatus === 'ready');
  const stopped = checked.flatMap(participant => {
    const label = stoppedCollectionLabel(participant.dataAvailability?.collection);
    return label ? [{ participant, label }] : [];
  });
  const stoppedIds = new Set(stopped.map(item => item.participant.participantId));
  const stale = checked.flatMap(participant => {
    if (stoppedIds.has(participant.participantId)) return [];
    const status = syncStatus(participant.dataAvailability?.wear, now);
    return status?.stale ? [{ participant, status }] : [];
  }).sort((a, b) => b.status.daysAgo - a.status.daysAgo);
  if (!stopped.length && !stale.length) return null;
  return <>
    {!!stopped.length && <Alert severity="error" sx={{ mb: 2 }} role="region" aria-label="Collections stopped">
      <AlertTitle>{stopped.length === 1 ? 'Collection has stopped for 1 participant' : `Collection has stopped for ${stopped.length} participants`}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {stopped.map(({ participant, label }) => <Typography component="li" variant="body2" key={participant.participantId}>
          <Box component="span" sx={{ fontWeight: 600 }}>{who(participant)}</Box> — {label.reason}. {label.action}
        </Typography>)}
      </Box>
    </Alert>}
    {!!stale.length && <Alert severity="warning" sx={{ mb: 2 }} role="region" aria-label="Participants not syncing">
      <AlertTitle>{stale.length === 1 ? '1 participant has' : `${stale.length} participants have`} not synced their watch for {SYNC_ALERT_DAYS}+ days</AlertTitle>
      <Typography variant="body2" sx={{ mb: .5 }}>Watches keep about 7 days of detailed data. Contact them before it is overwritten.</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {stale.map(({ participant, status }) => <Typography component="li" variant="body2" key={participant.participantId}>
          <Box component="span" sx={{ fontWeight: 600 }}>{who(participant)}</Box>
          {' '}— {status.daysAgo} days (last {status.source} {status.lastDate}; heart rate on {status.heartRateDays14} of the last 14 days)
        </Typography>)}
      </Box>
    </Alert>}
  </>;
}
